import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { router } from '@/app/router';
import PullToRefresh from '@/console/PullToRefresh';
import { applyRouteHead } from '@/lib/head';
import { watchChunkLoads } from '@/lib/chunkReload';
import { isEmbedded, startEmbed } from '@/lib/embed';

// Shown in a frame on another site (public/theme-init.js turned it on): keep ?embed=1 on every address inside,
// send links that leave the portal to a new tab, and size the frame for the host page's /embed.js (lib/embed.ts).
startEmbed();

// A page whose code cannot be loaded (mid-deploy, or a bad kept copy) reloads once (lib/chunkReload.ts).
watchChunkLoads();

/** The shell leaves in the same frame the first screen arrives (a .vc frame in the root), so nothing flashes in between. */
function dropShellWhenDrawn(root: HTMLElement): void {
  const shell = document.getElementById('vtp-shell');
  if (!shell) return;
  const done = () => {
    if (!root.querySelector('.vc')) return false;
    shell.remove();
    return true;
  };
  const watch = new MutationObserver(() => {
    if (done()) watch.disconnect();
  });
  watch.observe(root, { childList: true, subtree: true });
}

// The first in-app navigation turns the page entrance animations back on.
const stop = router.subscribe(() => {
  document.documentElement.classList.remove('vtp-boot');
  stop();
});

// Title and description per route, on first load and on every change of page (a screen with more to say,
// such as a record, sets its own once its data is in).
let headPath = window.location.pathname;
applyRouteHead(headPath);
router.subscribe((state) => {
  if (state.location.pathname === headPath) return;
  headPath = state.location.pathname;
  applyRouteHead(headPath);
});

const root = document.getElementById('root')!;
dropShellWhenDrawn(root);
createRoot(root).render(
  <StrictMode>
    <PullToRefresh />
    <RouterProvider router={router} />
  </StrictMode>,
);

// The map's tile cache (public/sw.js): elevation, vector and aerial tiles kept after the first view. Not inside
// another site's frame, where the browser keeps a separate cache for each site anyway.
if (import.meta.env.PROD && 'serviceWorker' in navigator && !isEmbedded())
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
